import './components/calculators/format';
import './components/calculators/finance';
import './components/calculators/calculator-products';
import './components/calculators/mortgage';
import './components/calculators/input';
import './components/calculators/products';

// =====================================================================
//  MORTGAGE / HOME LOAN — interactivity layer
//    1. Reads input values
//    2. Recomputes derived values via Calc.finance
//    3. Writes results into specific DOM targets (by id)
//    4. Toggles tab panels
//    5. Wires the pre-screen buttons
// =====================================================================
(function () {
  const fmt = Calc.format;
  const fin = Calc.finance;

  const REF_TENURES = [10, 15, 20, 25, 30, 35];
  const REF_PRICES = [200000, 300000, 400000, 500000, 750000, 1000000, 1500000, 2000000];

  const state = {
    sort: { key: 'best', dir: 'asc' },    // template's selected option sets the default
    propertyPrice: 500000,
    loanMargin: 0.90,
    interestRate: 0.0445,
    tenure: 30,
    income: 12000,
    age: 35,
    incomeType: 'salaried',
    activeTab: 'breakdown',
  };

  const loanAmount = () => state.propertyPrice * state.loanMargin;
  const downPayment = () => state.propertyPrice - loanAmount();
  // Net disposable income proxy (gross × 80%) for the NDI eligibility floor.
  const netIncome = () => state.income * 0.80;

  // -------------------------------------------------------------------
  //  DOM update helpers
  // -------------------------------------------------------------------
  function setText(id, value) {
    const el = document.getElementById(id);
    if (el) el.textContent = value;
  }
  // Write a value into a placeholder span (text is in HTML, JS only writes the value).
  function setVal(sel, val) {
    const el = document.querySelector(sel);
    if (el) el.textContent = val;
  }
  // Delegates to Calc.input.syncValue so Inputmask-attached inputs
  // receive reformatted display values when state changes programmatically.
  function syncInput(id, val) {
    Calc.input.syncValue(id, val);
  }
  // Escape dynamic strings before injecting as HTML (bank names / reason text
  // originate from the external product feed).
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  // -------------------------------------------------------------------
  //  Updates
  // -------------------------------------------------------------------
  function updateStatBand() {
    const calc = fin.reducingRate(loanAmount(), state.interestRate, state.tenure);
    const upfrontFees = fin.stampDutySPA(state.propertyPrice)
      + fin.stampDutyLoan(loanAmount())
      + fin.legalFees(state.propertyPrice)
      + fin.legalFees(loanAmount())
      + 600;
    const allInTotalCost = state.propertyPrice + calc.totalInterest + upfrontFees;

    setText('mt-stat-monthly',  fmt.money(calc.monthlyInstalment, 0));
    setText('mt-stat-upfront',  fmt.money(downPayment() + upfrontFees, 0));
    setText('mt-stat-interest', fmt.money(calc.totalInterest, 0));
    setText('mt-stat-allin',    fmt.money(allInTotalCost, 0));
  }

  function updateDerivedDisplays() {
    setVal('.mt-loan-num', loanAmount().toLocaleString());
    setVal('.mt-down-num', downPayment().toLocaleString());
  }

  function updateBreakdownTable() {
    const calc = fin.reducingRate(loanAmount(), state.interestRate, state.tenure);
    setText('mt-r-price',    fmt.money(state.propertyPrice));
    setVal('.mt-r-margin-num', (state.loanMargin * 100).toFixed(0));
    setText('mt-r-loan',     fmt.money(loanAmount()));
    setText('mt-r-down',     fmt.money(downPayment()));
    setVal('.mt-r-tenure-y', state.tenure);
    setVal('.mt-r-tenure-m', state.tenure * 12);
    setVal('.mt-r-rate-num', fmt.percent(state.interestRate));
    setText('mt-r-monthly',  fmt.money(calc.monthlyInstalment));
    setText('mt-r-interest', fmt.money(calc.totalInterest));
    setText('mt-r-total',    fmt.money(calc.totalRepayment));
  }

  function updateUpfrontTable() {
    const dp = downPayment();
    const sdSPA = fin.stampDutySPA(state.propertyPrice);
    const sdLoan = fin.stampDutyLoan(loanAmount());
    const lfSPA = fin.legalFees(state.propertyPrice);
    const lfLoan = fin.legalFees(loanAmount());
    const valuation = 600;
    const total = dp + sdSPA + sdLoan + lfSPA + lfLoan + valuation;

    setVal('.mt-up-dp-pct', (100 - state.loanMargin * 100).toFixed(0));
    setText('mt-up-dp',       fmt.money(dp));
    setText('mt-up-sd-spa',   fmt.money(sdSPA));
    setText('mt-up-sd-loan',  fmt.money(sdLoan));
    setText('mt-up-lf-spa',   fmt.money(lfSPA));
    setText('mt-up-lf-loan',  fmt.money(lfLoan));
    setText('mt-up-total',    fmt.money(total));
  }

  function updateReferenceGrid() {
    setText('mt-ref-rate', fmt.percent(state.interestRate));
    setVal('.mt-ref-ltv-num', (state.loanMargin * 100).toFixed(0));

    const tbody = document.getElementById('mt-ref-tbody');
    if (!tbody) return;

    tbody.innerHTML = REF_PRICES.map(price => {
      const loan = price * state.loanMargin;
      const isHighRow = price === state.propertyPrice;
      const cells = REF_TENURES.map(t => {
        const monthly = fin.reducingRate(loan, state.interestRate, t).monthlyInstalment;
        const isHighCol = t === state.tenure;
        return `<td class="${isHighCol ? 'highlight-col' : ''}">${fmt.number(monthly, 0)}</td>`;
      }).join('');
      return `<tr class="${isHighRow ? 'highlight' : ''}"><th>RM ${price.toLocaleString()}</th>${cells}</tr>`;
    }).join('');
  }

  function updateAmortization() {
    const host = document.getElementById('mt-amort');
    if (!host) return;
    Calc.amortView.renderInline(host, {
      startExpanded: false,
      amount: loanAmount(),
      rate: state.interestRate,
      years: state.tenure,
      method: 'reducing',
    });
  }

  // -------------------------------------------------------------------
  //  Bank comparison — eligibility + offers (mirrors the refinance calc)
  // -------------------------------------------------------------------
  function buildOffers() {
    const loan = loanAmount();
    const out = {};
    // Guard against malformed feed data: getCalculatorProducts()/json_encode
    // can hand us a non-array (object or null) when the upstream list is empty
    // or keyed oddly. Treat anything that isn't an array as "no lenders".
    const lenders = Array.isArray(Calc.data?.mortgageHomeLoan) ? Calc.data.mortgageHomeLoan : [];
    lenders.forEach(bank => {
      // Mortgages are reducing-balance. Compute at the user's loan amount
      // (price × LTV) and chosen tenure, using each bank's quoted rate.
      const calc = fin.reducingRate(loan, bank.interestRate / 100, state.tenure);
      const elig = checkEligibility(bank, calc.monthlyInstalment);
      out[bank.prod_id] = { bank, calc, eligible: elig.eligible, reasons: elig.reasons };
    });
    return out;
  }

  // Emits structured {code, params} reasons; the host page provides the
  // localized templates (see components/elig-templates.php).
  function checkEligibility(bank, newMonthly) {
    const e = bank.eligibility || {};
    const loan = loanAmount();
    const reasons = [];
    // Each rule is opt-in: a null/0/empty value means the feed didn't supply
    // the constraint, so we skip it rather than treat it as a hard zero.
    if (e.minAge && state.age < e.minAge) reasons.push({ code: 'min_age', params: { min: e.minAge } });
    if (e.maxAge && state.age > e.maxAge) reasons.push({ code: 'max_age', params: { max: e.maxAge } });
    if (e.minIncome && state.income < e.minIncome)
      reasons.push({ code: 'min_income', params: { min: e.minIncome } });
    const validEmpTypes = Array.isArray(e.employmentTypes)
      ? e.employmentTypes.filter(t => t != null && String(t).trim() !== '')
      : [];
    if (validEmpTypes.length && !validEmpTypes.includes(state.incomeType))
      reasons.push({ code: 'employment_not_accepted', params: { accepts: validEmpTypes.join(', ') } });
    if (bank.minAmount && loan < bank.minAmount)
      reasons.push({ code: 'min_loan', params: { min: bank.minAmount } });
    if (bank.maxAmount && loan > bank.maxAmount)
      reasons.push({ code: 'max_loan', params: { max: bank.maxAmount } });
    if (e.maxAge) {
      const yearsToAgeCap = e.maxAge - state.age;
      if (state.tenure > yearsToAgeCap)
        reasons.push({ code: 'tenure_exceeds_age', params: { yrs: yearsToAgeCap, age: state.age } });
    }
    if (e.dsrTiers?.length) {
      let maxDsr = 0;
      for (const t of e.dsrTiers) {
        if (state.income <= t.incomeUpTo) { maxDsr = t.maxDsr; break; }
      }
      const dsr = state.income > 0 ? newMonthly / state.income : 0;
      if (dsr > maxDsr)
        reasons.push({ code: 'dsr_exceeded', params: { dsr: (dsr * 100).toFixed(0), max: (maxDsr * 100).toFixed(0) } });
    }
    if (e.minNDIUrban) {
      const ndi = netIncome() - newMonthly;
      if (ndi < e.minNDIUrban)
        reasons.push({ code: 'ndi_below_min', params: { ndi: ndi.toFixed(0), min: e.minNDIUrban } });
    }
    return { eligible: reasons.length === 0, reasons };
  }

  // Render structured reasons to strings; degrade gracefully if the shared
  // helper isn't present (falls back to the template cache, then raw codes).
  function formatReasons(reasons) {
    if (typeof Calc?.eligibility?.renderReasons === 'function') {
      return Calc.eligibility.renderReasons(reasons);
    }
    const tplCache = Calc?.eligibility?._tplCache || {};
    return reasons.map(r => {
      const tpl = tplCache[r.code];
      if (!tpl) return r.code;
      return tpl.replace(/\{(\w+)\}/g, (_, k) => {
        const v = r.params && r.params[k];
        if (v == null) return '';
        return typeof v === 'number' ? v.toLocaleString() : String(v);
      });
    });
  }

  function updateBanks(offers) {
    // Best offer = lowest monthly instalment (no "current monthly" baseline
    // to compute savings against — this is a new purchase, not a refinance).
    const eligibleAll = Object.values(offers).filter(o => o.eligible);
    const bestSorted = eligibleAll.slice().sort((a, b) => a.calc.monthlyInstalment - b.calc.monthlyInstalment);
    const bestId = (bestSorted.length) ? bestSorted[0].bank.prod_id : null;
    const eligibleSorted = Calc.products.rank(
      eligibleAll.map((o, i) => ({ offer: o, index: i, name: o.bank.name, metrics: { best: o.calc.monthlyInstalment } })),
      state.sort.key, state.sort.dir
    ).map(e => e.offer);
    const ineligibleReasons = [];

    let eligibleCount = 0;
    const wraps = document.querySelectorAll('.bank-card-wrap');
    // Keyed on the element, not the id: feed prod_id is a STRING while dataset
    // reads coerce to a number, and `===` between them silently never matched.
    const wrapOf = new Map();
    wraps.forEach(wrap => {
      const rawId = wrap.dataset.bankId;
      const offer = offers[rawId];
      if (!offer) return;
      if (!offer.eligible) {
        wrap.classList.add('d-none');
        const rendered = formatReasons(offer.reasons);
        ineligibleReasons.push(`<strong>${esc(offer.bank.name)}:</strong> ${esc(rendered.join('; '))}`);
        return;
      }
      eligibleCount++;
      wrap.classList.remove('d-none');
      wrapOf.set(String(rawId), wrap);

      const m = wrap.querySelector('.bank-monthly');
      const intEl = wrap.querySelector('.bank-interest');
      const t = wrap.querySelector('.bank-total');
      if (m) m.textContent = fmt.money(offer.calc.monthlyInstalment, 0);
      if (intEl) intEl.textContent = fmt.money(offer.calc.totalInterest, 0);
      if (t) t.textContent = fmt.money(offer.calc.totalRepayment, 0);

    });

    Calc.products.applyOrder(
      wraps,
      eligibleSorted.map(o => ({ wrap: wrapOf.get(String(o.bank.prod_id)) })).filter(e => e.wrap),
      bestId != null ? wrapOf.get(String(bestId)) : null
    );

    const emptyState = document.querySelector('#mt-banks .mt-empty-state');
    if (emptyState) {
      emptyState.classList.toggle('d-none', eligibleCount > 0);
      emptyState.style.order = wraps.length;
    }

    // "Not eligible for" panel — list each excluded lender and why.
    const note = document.getElementById('mt-ineligible');
    const reasonsEl = document.getElementById('mt-ineligible-reasons');
    if (note && reasonsEl) {
      if (ineligibleReasons.length) {
        note.classList.remove('d-none');
        reasonsEl.innerHTML = ineligibleReasons.join('\n');
      } else {
        note.classList.add('d-none');
      }
    }
  }

  // -------------------------------------------------------------------
  //  Refresh: called on every input change
  // -------------------------------------------------------------------
  function refresh() {
    // Mirror state -> inputs (in case state was changed programmatically)
    syncInput('mt-price-input',  state.propertyPrice);
    syncInput('mt-margin-input', (state.loanMargin * 100).toFixed(0));
    syncInput('mt-rate-input',   (state.interestRate * 100).toFixed(2));
    syncInput('mt-tenure-input', state.tenure);

    updateDerivedDisplays();
    updateStatBand();
    updateBanks(buildOffers());

    // Only update the active tab's content (others stay correct until re-opened)
    if (state.activeTab === 'breakdown') {
      updateBreakdownTable();
      updateAmortization();
    } else if (state.activeTab === 'upfront') {
      updateUpfrontTable();
    } else if (state.activeTab === 'reference') {
      updateReferenceGrid();
    }
  }

  // -------------------------------------------------------------------
  //  Tabs
  // -------------------------------------------------------------------
  function activateTab(tabId) {
    state.activeTab = tabId;

    document.querySelectorAll('#mt-tabs .btn-tab').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tabId);
    });
    document.querySelectorAll('.tab-panel').forEach(panel => {
      panel.classList.toggle('d-none', panel.id !== `mt-tab-${tabId}`);
    });

    // Update freshly-shown content
    if (tabId === 'breakdown') {
      updateBreakdownTable();
      updateAmortization();
    } else if (tabId === 'upfront') {
      updateUpfrontTable();
    } else if (tabId === 'reference') {
      updateReferenceGrid();
    }
  }

  // -------------------------------------------------------------------
  //  Event wiring
  // -------------------------------------------------------------------
  function bindEvents() {
    // Listen to both input (typed numbers / masked input) and change (selects).
    // Calc.input.getValue returns a clean number whether the input has an
    // Inputmask attached (1,500,000 -> 1500000) or not (e.g. selects).
    ['input', 'change'].forEach(evt => document.addEventListener(evt, (e) => {
      const id = e.target.id;
      const v = Calc.input.getValue(e.target);
      if (id === 'mt-price-input')  { state.propertyPrice = v || 0;       refresh(); }
      if (id === 'mt-margin-input') { state.loanMargin = (v || 0) / 100;  refresh(); }
      if (id === 'mt-rate-input')   { state.interestRate = (v || 0) / 100; refresh(); }
      if (id === 'mt-tenure-input') { state.tenure = v || 1;              refresh(); }
      if (id === 'mt-income')       { state.income = v || 0;              refresh(); }
      if (id === 'mt-age')          { state.age = v || 0;                 refresh(); }
      if (id === 'mt-itype')        { state.incomeType = e.target.value;  refresh(); }
    }));

    document.querySelectorAll('#mt-tabs .btn-tab').forEach(btn => {
      btn.addEventListener('click', () => activateTab(btn.dataset.tab));
    });
  }

  // -------------------------------------------------------------------
  //  Init
  // -------------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', () => {
    bindEvents();
    const initialSort = Calc.products.bindSort('#mt-sort-select', (sort) => {
      state.sort = sort;
      refresh();
    });
    if (initialSort) state.sort = initialSort;
    refresh();
  });
})();